import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import {
  AiOutlineFilePdf,
  AiOutlineFileImage,
  AiOutlineFileWord,
  AiOutlineFileExcel,
  AiOutlineFileUnknown,
} from "react-icons/ai";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import MultiFileUploader from "../../Componentes/Utils/MultiFileUploader";
import useApi from "../../hooks/useApi";

const GET_ANEXOS_ENDPOINT = "/Painel/API/InventarioTI/GetAnexosAtivo/";
const ADD_ANEXO_ENDPOINT = "/Painel/API/InventarioTI/AddAnexoAtivo/";
const DELETE_ANEXO_ENDPOINT = "/Painel/API/InventarioTI/DeleteAnexoAtivo/";

const Titulo = styled.h3`
  margin: 0 0 6px 0;
  padding: 0 0 12px 2px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 15px;
  font-weight: 700;
  color: #263b4b;
  letter-spacing: 0.02em;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

const Subtitulo = styled.p`
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
`;

const ErrorMsg = styled.p`
  margin: 0;
  font-size: 13px;
  color: #b91c1c;
  text-align: center;
`;

const SecaoAnexos = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AnexosLabel = styled.p`
  margin: 0 0 2px 2px;
  font-size: 12px;
  font-weight: 700;
  color: #263b4b;
  letter-spacing: 0.02em;
`;

const SemAnexos = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  padding: 10px 12px;
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  text-align: center;
`;

const CardAnexo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: ${(p) => (p.$confirming ? "#fff5f5" : "#f8fafc")};
  border: 1px solid ${(p) => (p.$confirming ? "#fecaca" : "#e2e8f0")};
  border-radius: 10px;
  min-width: 0;
`;

const AnexoIconWrap = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
`;

const AnexoInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AnexoNome = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AnexoTamanho = styled.span`
  font-size: 11px;
  color: #64748b;
`;

const AnexoAcoes = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BotaoAnexo = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
  background-color: ${(p) =>
    p.$danger ? "rgba(185,28,28,0.10)" : "rgba(29,78,216,0.08)"};

  &:hover {
    background-color: ${(p) =>
      p.$danger ? "rgba(185,28,28,0.20)" : "rgba(29,78,216,0.18)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ConfirmLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #b91c1c;
  white-space: nowrap;
`;

const BotaoConfirm = styled.button`
  all: unset;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#dc2626")};
  color: ${(p) => (p.$cancel ? "#333" : "#fff")};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BotaoEnviarAnexos = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background-color: #263b4b;
  color: #fff;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background-color: #1e2f3c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function formatarTamanho(bytes) {
  if (bytes == null || bytes === "" || Number.isNaN(Number(bytes))) return null;
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function nomeArquivo(anexo) {
  if (anexo?.nome_original) return anexo.nome_original;
  const parts = String(anexo?.caminho ?? "").split("/");
  return parts[parts.length - 1] || "Arquivo";
}

function getFileIcon(filename) {
  const name = String(filename ?? "").toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(name))
    return <AiOutlineFileImage size={20} color="#4a90e2" />;
  if (name.endsWith(".pdf"))
    return <AiOutlineFilePdf size={20} color="#e63946" />;
  if (/\.(doc|docx)$/i.test(name))
    return <AiOutlineFileWord size={20} color="#2a6db0" />;
  if (/\.(xls|xlsx)$/i.test(name))
    return <AiOutlineFileExcel size={20} color="#207245" />;
  return <AiOutlineFileUnknown size={20} color="#555" />;
}

function labelEquipamento(equipamento) {
  if (!equipamento) return "";
  const partes = [
    equipamento.descricao && equipamento.descricao !== "—"
      ? equipamento.descricao
      : "",
    equipamento.patrimonio && equipamento.patrimonio !== "—"
      ? `Patrimônio ${equipamento.patrimonio}`
      : "",
  ].filter(Boolean);
  return partes.join(" • ") || `ID ${equipamento.id}`;
}

export default function ModalAnexosEquipamento({ equipamento, onClose }) {
  const api = useApi();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [anexos, setAnexos] = useState([]);
  const [arquivosPendentes, setArquivosPendentes] = useState([]);
  const [enviandoAnexos, setEnviandoAnexos] = useState(false);
  const [confirmDeleteAnexoId, setConfirmDeleteAnexoId] = useState(null);
  const [deletingAnexoId, setDeletingAnexoId] = useState(null);

  const carregarAnexos = useCallback(async () => {
    if (!equipamento?.id) return;

    setLoading(true);
    setErro("");

    try {
      const { data } = await api.get(GET_ANEXOS_ENDPOINT, {
        params: { id_ativo: equipamento.id },
      });

      if (!data?.success) {
        throw new Error(
          data?.message || "Não foi possível carregar os anexos.",
        );
      }

      setAnexos(Array.isArray(data.anexos) ? data.anexos : []);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Erro ao carregar anexos.";
      setErro(msg);
      setAnexos([]);
    } finally {
      setLoading(false);
    }
  }, [api, equipamento?.id]);

  useEffect(() => {
    carregarAnexos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAnexo = async (anexoId) => {
    setDeletingAnexoId(anexoId);
    try {
      const { data } = await api.post(DELETE_ANEXO_ENDPOINT, { id: anexoId });

      if (data?.success) {
        setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
        setConfirmDeleteAnexoId(null);
      } else {
        alert(data?.message || "Não foi possível remover o anexo.");
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao remover anexo.");
    } finally {
      setDeletingAnexoId(null);
    }
  };

  const handleEnviarAnexos = async () => {
    if (arquivosPendentes.length === 0 || enviandoAnexos || !equipamento?.id)
      return;

    setEnviandoAnexos(true);
    try {
      const formData = new FormData();
      formData.append("id_ativo", equipamento.id);
      arquivosPendentes.forEach((item) =>
        formData.append("files[]", item.file),
      );

      const { data } = await api.post(ADD_ANEXO_ENDPOINT, formData);

      if (data?.success) {
        const novos = Array.isArray(data.inseridos) ? data.inseridos : [];
        setAnexos((prev) => [...novos, ...prev]);
        setArquivosPendentes([]);

        if (Array.isArray(data.erros) && data.erros.length > 0) {
          alert(
            `${data.message || "Anexos enviados."}\n\nAlguns arquivos não foram salvos:\n${data.erros.join("\n")}`,
          );
        }
      } else {
        alert(data?.message || "Erro ao enviar anexos.");
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao enviar anexos.");
    } finally {
      setEnviandoAnexos(false);
    }
  };

  if (!equipamento) return null;

  return (
    <Overlay state={true} changeState={onClose}>
      <Modal header close={onClose} size="md">
        <Titulo>VISUALIZAR ANEXOS</Titulo>
        <Subtitulo>{labelEquipamento(equipamento)}</Subtitulo>

        {loading && <SmallLoading text="Carregando anexos…" />}

        {!loading && erro && <ErrorMsg>{erro}</ErrorMsg>}

        {!loading && !erro && (
          <Container>
            <SecaoAnexos>
              <AnexosLabel>ANEXOS ({anexos.length})</AnexosLabel>

              {anexos.length === 0 ? (
                <SemAnexos>Nenhum anexo cadastrado.</SemAnexos>
              ) : (
                anexos.map((anexo) => {
                  const nome = nomeArquivo(anexo);
                  const tamanho = formatarTamanho(anexo.tamanho);
                  const isConfirming = confirmDeleteAnexoId === anexo.id;
                  const isDeleting = deletingAnexoId === anexo.id;

                  return (
                    <CardAnexo
                      key={anexo.id ?? anexo.caminho}
                      $confirming={isConfirming}
                    >
                      <AnexoIconWrap>{getFileIcon(nome)}</AnexoIconWrap>

                      <AnexoInfo>
                        <AnexoNome title={nome}>{nome}</AnexoNome>
                        {tamanho && <AnexoTamanho>{tamanho}</AnexoTamanho>}
                      </AnexoInfo>

                      {isConfirming ? (
                        <ConfirmBox>
                          <ConfirmLabel>Confirmar?</ConfirmLabel>
                          <BotaoConfirm
                            type="button"
                            $cancel
                            disabled={isDeleting}
                            onClick={() => setConfirmDeleteAnexoId(null)}
                          >
                            NÃO
                          </BotaoConfirm>
                          <BotaoConfirm
                            type="button"
                            disabled={isDeleting}
                            onClick={() => handleDeleteAnexo(anexo.id)}
                          >
                            {isDeleting ? "…" : "SIM"}
                          </BotaoConfirm>
                        </ConfirmBox>
                      ) : (
                        <AnexoAcoes>
                          <BotaoAnexo
                            as="a"
                            href={anexo.caminho}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Baixar anexo"
                          >
                            <FiDownload size={14} />
                          </BotaoAnexo>
                          <BotaoAnexo
                            $danger
                            type="button"
                            title="Remover anexo"
                            onClick={() => setConfirmDeleteAnexoId(anexo.id)}
                          >
                            <FiTrash2 size={14} />
                          </BotaoAnexo>
                        </AnexoAcoes>
                      )}
                    </CardAnexo>
                  );
                })
              )}
            </SecaoAnexos>

            <SecaoAnexos>
              <AnexosLabel>ADICIONAR ANEXOS</AnexosLabel>
              <MultiFileUploader
                files={arquivosPendentes}
                setFiles={setArquivosPendentes}
                maxFiles={10}
                maxSizeMB={10}
              />
              <BotaoEnviarAnexos
                type="button"
                disabled={arquivosPendentes.length === 0 || enviandoAnexos}
                onClick={handleEnviarAnexos}
              >
                {enviandoAnexos ? "ENVIANDO…" : "ENVIAR ANEXOS"}
              </BotaoEnviarAnexos>
            </SecaoAnexos>
          </Container>
        )}
      </Modal>
    </Overlay>
  );
}